import React, { useEffect, useState } from "react";
import "../../../../../style/components/file-upload.scss";
import uploadIcon from "../../../../../assets/icons/upload.svg";
import infoIcon from "../../../../../assets/icons/info-2.svg";
import videoIcon from "../../../../../assets/icons/video-library.svg";

export enum MediaType {
  Image = "image",
  Video = "video",
}
interface FileUploadProps {
  accept: string;           // files which will be accepted by the input
  idName: string;           // id name of the input
  infoText: string;         // text for info box
  maxFileCount?: number;    // max file count which can be selected
  showFilesView?: boolean;  // list of the files are visible for the user
  mediaType: MediaType;     // media type of image or video, for display purposes
  onFilesSelected: (files: { source: File | string }[]) => void; // notify the parent
  files: { source: File | string }[]; // bindable file list from the parent
  setFiles: React.Dispatch<React.SetStateAction<{ source: File | string }[]>>; // updates the file array
}

const FileUpload: React.FC<FileUploadProps> = ({
  accept,
  idName,
  infoText,
  mediaType,
  onFilesSelected,
  files,
  setFiles,
  showFilesView = true,
  maxFileCount = 20,
}) => {
  const [isOpen, setIsOpen] = useState(true);
  const [dragActive, setDragActive] = useState(false);
  const [showInfo, setShowInfo] = useState(false);
  const [displayItems, setDisplayItems] = useState(true);
  const [fileTypes, setFileTypes] = useState<string[]>([]);
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [uploadText] = useState<string[]>([
    "Hier Hochladen oder die Datei Ziehen und Ablegen",
    " Datei ausgewählt.",
    " Dateien ausgewählt.",
  ]);

  //#region string manipulation
  const getFileTypeString = () => {
    if (fileTypes.length === 1)
      return fileTypes[0]
        ? fileTypes[0]
        : "Mehrere Bilderformate sind erlaubt.";
    else if (fileTypes.length > 1) {
      return (
        fileTypes.slice(0, -1).join(", ") +
        " oder " +
        fileTypes[fileTypes.length - 1]
      );
    } else {
      return "Mehrere Bilderformate sind erlaubt.";
    }
  };
  //#endregion

  //#region calc
  // calculates the height for items when closing button clicked
  const calcClosedFilesHeight = (): number => {
    return files.slice(0, 2).reduce((totalHeight, file) => {
      if (mediaType === MediaType.Image) {
        if (file.source instanceof File) {
          return totalHeight + 3.1; // Add 3.1 for File type
        } else if (typeof file.source === "string") {
          return totalHeight + 5; // Add 5 for string type
        }
      } else if (mediaType === MediaType.Video) {
        return totalHeight + 7;
      }
      return totalHeight;
    }, 0);
  }
  //#endregion

  //#region update
  const updateFiles = (selectedFiles: { source: File | string }[]) => {
    if (selectedFiles) {
      setFiles((prevFiles) => {
        const filteredFiles = selectedFiles.filter((file) => {
          // process only local files (ignore strings, which represent URLs)
          if (!(file.source instanceof File)) return true;

          const fileSource = file.source as File;
          if (!fileSource.name) return false;

          const lastDotIndex = fileSource.name.lastIndexOf(".");
          if (lastDotIndex === -1) return false; // no extension found

          const fileExtension = fileSource.name
            .substring(lastDotIndex + 1)
            .toUpperCase();
          return fileTypes.includes(fileExtension);
        });

        // convert filtered files into the structured format
        const newFiles = filteredFiles.map((file) => ({
          source: file.source as File | string,
        }));

        // combine with previous files
        const updatedFiles: { source: File | string }[] = [
          ...prevFiles,
          ...newFiles,
        ].slice(0, maxFileCount);

        // notify the parent with only the local files
        onFilesSelected(updatedFiles);

        return updatedFiles;
      });
    }
  };

  //#endregion
  //#region handle
  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    // replace of type File:    
    // const editFiles: { source: File | string }[] =
    //   files.filter((file) => !(file.source instanceof File)) || [];

    // append newly added files and combine them with preexisting files
    setFiles(files); 
    setIsOpen(true);
    const selectedFiles: { source: File | string }[] = 
      Array.from( event.target.files || [])
        .map((file) => ({
          source: file,
    }));
    updateFiles(selectedFiles ? selectedFiles : files);
  };

  //#region move files
  const handleMoveDragStart = (index: number) => {
    if (isOpen) setDraggedIndex(index);
  };

  const handleMoveDrop = (index: number) => {
    if (draggedIndex !== null && draggedIndex !== index) {
      const reorderedImages = [...files];
      console.log("dropped: " + reorderedImages[0].source);
      const [movedImage] = reorderedImages.splice(draggedIndex, 1);
      reorderedImages.splice(index, 0, movedImage);
      console.log("dropped: " + reorderedImages[0].source);
      setFiles(reorderedImages);
      setDraggedIndex(null);
    }
  };

  const handleMoveDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
  };
  //#endregion

  //#region drop files
  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setDragActive(true);
  };

  const handleDragLeave = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setDragActive(false);
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setDragActive(false);

    const droppedFiles = Array.from(event.dataTransfer.files).slice(
      0,
      maxFileCount
    );
    if (droppedFiles.length) {
      const formattedFiles = droppedFiles.map((file) => ({
        source: file,
      })); // convert to the expected format
      updateFiles(formattedFiles);

      if (droppedFiles.length > 1) setIsOpen(true);
    }
  };

  //#endregion
  //#endregion

  //#region remove
  const removeFile = (index: number) => {
    const updatedFiles = files.filter((_, i) => i !== index);
    setFiles(updatedFiles);
  };
  const removeAllFiles = (fileType: string | null = null) => {
    if (!fileType)
      setFiles([]);
    else if (fileType.toLowerCase() === "string") {
      const newFiles = files.filter((file) => !(file.source instanceof File)) || [];
      setFiles(newFiles)
    } else if (fileType.toLowerCase() === "file") {
      const newFiles = files.filter((file) => file.source instanceof File) || [];
      setFiles(newFiles)
    }
  };
  //#endregion

  //#region useeffect
  useEffect(() => {
    if (accept === "image/*") {
      setFileTypes(Array.from(["Mehrere Bilderformate sind erlaubt."]));
    } else {
      // process and split the accept string by commas
      const splitAccept = accept.split("/")[1]
        ? accept.split("/")[1]
        : accept.split("/")[0];
      const types: Array<string> | string = splitAccept
        ? splitAccept.split(",").map((type) => {
            return type ? type.slice(1, type.length).toUpperCase() : "";
          })
        : "Nicht definiert";

      if (Array.isArray(types)) {
        const limitedTypes = types.slice(0, 4); // limit to 4 file types
        if (limitedTypes.length > 1) {
          setFileTypes(limitedTypes);
        } else {
          setFileTypes(limitedTypes);
        }
      } else if (types.length === 1) {
        setFileTypes(Array.from([types]));
      } else {
        setFileTypes(Array.from(["Mehrere Bilderformate sind erlaubt."]));
      }
    }
  }, [accept]);
  useEffect(() => {
    if (!isOpen) {
      const timer = setTimeout(() => setDisplayItems(false), 600); // hide after 500ms
      return () => clearTimeout(timer);
    } else {
      setDisplayItems(true); // show items instantly when open
    }
  }, [isOpen]);
  //#endregion

  return (
    <div className="file-upload-container">
      <div
        className={`file-upload-label ${
          files.length >= maxFileCount ? "drag-block" : ""
        } ${dragActive ? "drag-active" : ""}`}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        <label
          htmlFor={idName}
          className={`file-upload-label ${
            maxFileCount > -1 && files.length >= maxFileCount
              ? "blocked"
              : "responsive"
          }`}
        >
          <div className="upload-content">
            <img
              className="upload-icon prevent-select"
              src={uploadIcon}
              alt="upload-icon"
            />
            <span className="upload-text font-semibold prevent-select">
              {files.length > 0
                ? files.length === 1
                  ? `Eine ${uploadText[1]}`
                  : `${files.length} ${uploadText[2]}`
                : uploadText[0]}
            </span>
            <p className="upload-instructions prevent-select">
              {getFileTypeString()}
            </p>
          </div>
          <input
            id={idName}
            type="file"
            accept={accept}
            className="file-input"
            onChange={handleFileChange}
            multiple={maxFileCount > 1}
            required
            disabled={maxFileCount > -1 && files.length >= maxFileCount}
          />
        </label>
      </div>

      {files.length > 0 && showFilesView && (
        <div style={{ width: "100%"}}>
          <div className={`info-container ${showInfo ? "visible" : "hidden"}`}>
            {!showInfo && files.length > 1 && (
              <button
                className="icon-button"
                style={{ width: "2rem", height: "2rem" }}
                onClick={() => setShowInfo((prev) => !prev)}
                onDragStart={e => e.preventDefault()}
              >
                <img src={infoIcon} alt="Avatar Icon" className="icon" />
              </button>
            )}
            {showInfo && (
              <div className="info-box open">
                <button
                  className="close-info"
                  onClick={() => setShowInfo(false)}
                >
                  ✖
                </button>
                <div
                  className="info-text"
                  dangerouslySetInnerHTML={{ __html: infoText }}
                ></div>
              </div>
            )}
          </div>
          {files.length > 2 && (
            <button
              className="toggle-button icon-button"
              onClick={(e) => {
                e.preventDefault();
                setIsOpen((prev) => !prev);
              }}
            >
              <h1
                style={{
                  transform: isOpen ? "rotate(0deg)" : "rotate(180deg)",
                  transition: "transform 0.3s ease",
                }}
              >
                ▼
              </h1>
            </button>
          )}

          <div className={`file-list ${isOpen ? "open" : ""}`}
                style={isOpen ? {} : { maxHeight: `${calcClosedFilesHeight()}rem`, minHeight: `${calcClosedFilesHeight()}rem`, 
                  transition: "max-height .5s ease-out"}}>
            {files.map((file, index) => (
              <div
                key={index}
                className={`file-item ${mediaType === MediaType.Image ? "file-item-image" : ""}  ${isOpen ? "open" : "closed"} ${
                  displayItems ? "" : "none"
                }`}
                draggable={isOpen}
                onDragStart={() =>
                  isOpen ? handleMoveDragStart(index) : "return false;"
                }
                onDragOver={handleMoveDragOver}
                onDrop={() =>
                  isOpen ? handleMoveDrop(index) : "return false;"
                }
              >
                {mediaType === MediaType.Image && (
                  <div
                    className={`image-item ${
                      file.source instanceof File ? "" : "edit"
                    } ${isOpen ? "open" : "closed"}`}
                  >
                    <img
                      src={
                        file.source instanceof File
                          ? URL.createObjectURL(file.source)
                          : file.source
                      }
                      alt={
                        file.source instanceof File
                          ? file.source.name
                          : "pic" + index
                      }
                      draggable={false}
                      className={`image-size ${
                        file.source instanceof File ? "" : "edit"
                      }`}
                      onError={(e: any) => {
                        e.target.onerror = null; // Prevent infinite loop
                        e.target.src = videoIcon;
                      }}
                    />
                  </div>
                )}
                {mediaType === MediaType.Video && (
                  <video
                    controls={isOpen}
                    muted
                    loop
                    key={
                      file.source instanceof File
                        ? URL.createObjectURL(file.source)
                        : file.source
                    }
                    className={`video-item ${
                      file.source instanceof File ? "" : "edit"
                    } ${isOpen ? "open" : "closed"}`}
                  >
                    <source
                      src={
                        file.source instanceof File
                          ? URL.createObjectURL(file.source)
                          : file.source
                      }
                      draggable={false}
                      type="video/mp4"
                      className="video-size"
                      onError={(e: any) => {
                        e.target.onerror = null; // prevent infinite loop
                        e.target.src = videoIcon;
                      }}
                    />
                  </video>
                )}

                <span className="filename" style={{ display: "inline-block" }}>
                  {file.source instanceof File ? file.source.name : index}
                </span>
                <div className="remove-button-container">
                  <button
                    className={`remove-button ${isOpen ? "open" : "closed"}`}
                    onClick={(e) => {
                      e.preventDefault();
                      if (isOpen) {
                        removeFile(index);
                      }
                    }}
                  >
                    ✖
                  </button>
                </div>
              </div>
            ))}
            {files.length > 1 &&
              files.some((source) => source.source instanceof String) && (
                <div
                  style={{ paddingTop: "1rem" }}
                  className={`remove-button-container ${
                    isOpen ? "open" : "closed"
                  } ${displayItems ? "" : "none"}`}
                >
                  <button
                    className="remove-button"
                    onClick={() => { 
                      setIsOpen(false); 
                      setTimeout(() => 
                        removeAllFiles() 
                      // specific: files.some((soure) => soure.source instanceof File) ? "String" : null
                      // but causes for some reason submit
                      , 450); 
                    }}
                  >
                    Zurücksetzen
                  </button>
                </div>
              )}
          </div>
        </div>
      )}

      {/* was trying to display a tooltip
             <div className="file-list">
                {files.length > 0 && (
                    <div className="tooltip">
                        {files.map((file, index) => (
                            <div key={index} className="file-item">{file.name}</div>
                        ))}
                    </div>
                )}
            </div> */}
    </div>
  );
};

export default FileUpload;
